/**
 * iQuickCalc Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { RichSummaryReport, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues, ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid } from '../shared';
import { analyze } from './api';
import type { ChartKey, QuickCalcResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, LOAD_TYPES, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'Quick Calc',
          length: '10',
          tribWidth: '5',
          hingeLength: '5',
          totalWeight: '5*12*10',
          klmFactor: '0.72',
          Ru: '9',
          Xe: '1.0',
          LoadType: '0',
          Pa: '4.0',
          td: '14.0'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, QuickCalcResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, QuickCalcResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, QuickCalcResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[PRINT_CHART_ASPECT[key]],
    errorMessage: 'Unable to prepare charts for printing. Please wait for the charts to finish rendering and try again.'
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, QuickCalcResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    { kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Length' },
    { kind: 'input', name: 'tribWidth', label: 'Tributary Width', unit: 'ft', helpText: 'Enter the tributary width', placeholder: 'Tributary Width' },
    { kind: 'input', name: 'hingeLength', label: 'Hinge Length', unit: 'ft', helpText: 'Enter the hinge length', placeholder: 'Hinge Length' },
    { kind: 'input', name: 'totalWeight', label: 'Total Weight', unit: 'lbs', helpText: 'Enter the total weight', placeholder: 'Total Weight' },
    { kind: 'input', name: 'klmFactor', label: 'KLM Factor', unit: '', helpText: 'Enter the KLM factor', placeholder: 'KLM Factor' },
    { kind: 'input', name: 'Ru', label: 'Ultimate Resistance', unit: 'kips', helpText: 'Enter the ultimate resistance', placeholder: 'Ultimate Resistance' },
    { kind: 'input', name: 'Xe', label: 'Elastic Displacement', unit: 'in', helpText: 'Enter the elastic displacement', placeholder: 'Elastic Displacement' },
    { kind: 'dropdown', name: 'LoadType', label: 'Load Type', options: LOAD_TYPES, helpText: 'Select the load type...' },
    { kind: 'input', name: 'Pa', label: 'Peak Pressure', unit: 'psi', helpText: 'Enter the peak pressure', placeholder: 'Peak Pressure' },
    { kind: 'input', name: 'td', label: 'Duration', unit: 'msec', helpText: 'Enter the duration', placeholder: 'Duration' }
  ]);
  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Engineering Analysis Module</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-2 sm:p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />
          <div className="analysis-app__actions flex w-full flex-col gap-2 mt-2 sm:mt-1 sm:flex-row sm:flex-wrap">
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {' '}
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}{' '}
            </button>
            <button className="submit-button w-full sm:w-auto sm:flex-1" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {' '}
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>{' '}
            <GenerateLinkButton formData={values} className="w-full sm:w-auto" />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 xl:grid-cols-2 items-stretch gap-3 m-2 sm:m-4 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] border border-black print:bg-white print:text-black">
              <div className="flex flex-col h-full min-h-0 ">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iQuick Calc'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 overflow-hidden">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
